/**
 * THE stored-variable precedence, highest first. Every resolver, display path,
 * script snapshot, codegen list and the MCP index builds from this one list
 * (review N5, 28.09.2026: thirteen hand-written copies could drift).
 *
 * Above these sit two non-stored layers that only Send / Run have: script-local
 * variables and the session overlay (COL-03). Below them, the dynamic `$…`
 * fallback. Neither is part of this list.
 *
 * `folder` is the flat nearest-first list from utils/folderVariables, so a
 * first-hit `.find` over it is nearest-folder-wins.
 */
// Environment ABOVE collection (owner, 16.10.2026), as Postman and Bruno do:
// the environment you select overrides a collection's default of the same
// name — that is what switching dev/staging/prod over one collection is for.
// Until 16.10 a collection variable beat it, so switching environment seemed
// to do nothing for any name the collection also defined.
export const SCOPE_PRECEDENCE = Object.freeze(['folder', 'environment', 'collection', 'workspace', 'global']);

/** Same list, lowest first — for "last write wins" consumers (script maps, codegen Maps). */
export const SCOPE_PRECEDENCE_LOWEST_FIRST = Object.freeze([...SCOPE_PRECEDENCE].reverse());

/**
 * The winning enabled row for `name`.
 *
 * @param {string} name
 * @param {{folder?: Array, collection?: Array, environment?: Array, workspace?: Array, global?: Array}} scopes
 * @returns {{scope: string, variable: object}|null}
 */
export function lookupScopedVariable(name, scopes = {}) {
  for (const scope of SCOPE_PRECEDENCE) {
    const list = scopes[scope];
    if (!Array.isArray(list)) continue;
    const variable = list.find((v) => v?.enabled && v?.key === name);
    if (variable) return { scope, variable };
  }
  return null;
}

/**
 * Concatenate the scope lists lowest priority first, so a consumer that fills a
 * Map in sequence ends with the winning row per key. `folder` (nearest-first)
 * is reversed so the NEAREST folder lands last.
 */
export function scopeRowsLowestFirst(scopes = {}) {
  const out = [];
  for (const scope of SCOPE_PRECEDENCE_LOWEST_FIRST) {
    const list = scopes[scope];
    if (!Array.isArray(list)) continue;
    out.push(...(scope === 'folder' ? [...list].reverse() : list));
  }
  return out;
}
